import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import Services from "../services/Services";
import { broadcastAuthMessage, subscribeAuthMessage } from "../utils/authChannel";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    return localStorage.getItem("token") || sessionStorage.getItem("token") || null;
  });

  const [user, setUser] = useState(() => {
    try {
      const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  });

  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [myNotes, setMyNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [tokenBalance, setTokenBalance] = useState(null);

  const [subscriptionType, setSubscriptionType] = useState(() => {
    // Try to get from user object initially
    try {
      const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
      if (userStr) {
        const userData = JSON.parse(userStr);
        return userData?.subscriptionPackageType || "social-hive";
      }
    } catch {}
    return "social-hive";
  });

  const isPro = subscriptionType === "professional";

  const isAuthenticated = Boolean(token && user);

  const getUserId = useCallback(() => {
    return user?.id || user?.userID || user?.userId || null;
  }, [user]);

  const getDisplayName = useCallback(() => {
    const firstName = user?.firstName || user?.firstname || user?.name || "";
    const lastName = user?.lastName || user?.lastname || "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    return fullName || user?.email || "User";
  }, [user]);

  const getAvatarUrl = useCallback(() => {
    return user?.userAvatar || user?.avatarUrl || user?.avatar || user?.profileImage || null;
  }, [user]);

  const fetchWorkspaces = useCallback(async () => {
    if (!token) {
      return;
    }
    const userId = user?.id || user?.userID || user?.userId;
    if (!userId) {
      return;
    }
    try {
      const res = await Services.GetUserAuthorizations(token, userId);
      if (res?.data?.status && Array.isArray(res.data.result)) {
        setWorkspaces(res.data.result);
      } 
    } catch (error) {
      console.error("[AuthContext] Error fetching workspaces:", error);
    }
  }, [token, user]);

  const fetchMyNotes = useCallback(async () => {
    if (!token) return;
    try {
      const res = await Services.GetMyNotes(token);
      if (res?.data?.status) {
        const result = res.data.result;
        const folderNotes =
          result?.folders?.flatMap(
            (folder) =>
              folder.notes?.map((note) => ({
                ...note,
                folderTitle: folder.title,
              })) || []
          ) || [];
        const ungroupedNotes = result?.notes || [];
        setMyNotes([...folderNotes, ...ungroupedNotes]);
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  }, [token]);

  const fetchTokenBalance = useCallback(async () => {
    if (!token) return;
    try {
      const res = await Services.GetTokenBalance(token);
      if (res?.data?.status && res.data.result) {
        setTokenBalance(res.data.result);
      }
    } catch (error) {
      console.error("[AuthContext] Error fetching token balance:", error);
    }
  }, [token]);

  const refreshTokenBalance = useCallback(() => {
    fetchTokenBalance();
  }, [fetchTokenBalance]);

  const fetchSubscription = useCallback(async () => {
    if (!token) return;
    try {
      const res = await Services.GetUserSubscriptionInfos(token);
      if (res?.data?.status && res.data.result?.subscriptionPackageType) {
        setSubscriptionType(res.data.result.subscriptionPackageType);
        return;
      }
    } catch (error) {
      console.error("[AuthContext] Error fetching subscription:", error);
    }
    // Fallback: try to get from user object
    if (user?.subscriptionPackageType) {
      setSubscriptionType(user.subscriptionPackageType);
    }
  }, [token, user]);

  const login = useCallback((userData, userToken, remember = true) => {
    setToken(userToken);
    setUser(userData);

    // Set subscription from user data if available
    if (userData?.subscriptionPackageType) {
      setSubscriptionType(userData.subscriptionPackageType);
    }

    // Karşı depoyu MUTLAKA temizle. Aksi halde eski bir girişten kalan token
    // yenisini gölgeliyordu: başlangıç okuması `localStorage || sessionStorage`
    // sırasıyla yapıldığı için, "hatırla" ile girip sonra "hatırlama" ile giren
    // kullanıcı bir sonraki açılışta BAYAT localStorage token'ıyla açılıyordu.
    if (remember) {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
      localStorage.setItem("token", userToken);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.setItem("token", userToken);
      sessionStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("rememberMe", "false");
    }

    broadcastAuthMessage({
      type: "LOGIN",
      token: userToken,
      user: userData,
      rememberMe: remember,
    });

    window.dispatchEvent(new Event("app-token-changed"));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setWorkspaces([]);
    setMyNotes([]);

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    // `rememberMe` KASITLI olarak duruyor: bu bir kimlik bilgisi değil, sadece
    // kutunun tercihi. Silinirse "beni hatırlama" diyen kullanıcının seçimi her
    // çıkışta unutuluyor ve kutu yeniden işaretli geliyordu.

    broadcastAuthMessage({ type: "LOGOUT" });
    window.dispatchEvent(new Event("app-token-changed"));
  }, []);

  // Listen to auth broadcasts from other tabs
  useEffect(() => {
    const unsubscribe = subscribeAuthMessage((data) => {
      if (!data || typeof data !== "object") return;

      if (data.type === "LOGIN" && data.token && data.user) {
        setToken(data.token);
        setUser(data.user);
      }

      if (data.type === "LOGOUT") {
        setToken(null);
        setUser(null);
        setWorkspaces([]);
        setMyNotes([]);
      }
    });

    return () => unsubscribe();
  }, []);

  // Initialize and validate session
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      if (token) {
        await fetchWorkspaces();
        await fetchMyNotes();
        await fetchSubscription();
        await fetchTokenBalance();
      }

      setIsLoading(false);
    };

    init();
  }, [token, fetchWorkspaces, fetchMyNotes, fetchSubscription, fetchTokenBalance]);

  const value = {
    token,
    user,
    isAuthenticated,
    isLoading,
    workspaces,
    myNotes,
    selectedNote,
    setSelectedNote,
    isPro,
    subscriptionType,
    tokenBalance,
    refreshTokenBalance,
    login,
    logout,
    getUserId,
    getDisplayName,
    getAvatarUrl,
    fetchWorkspaces,
    fetchMyNotes,
    fetchSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
